import { print } from "graphql";
import _ from 'lodash'
import HelperEncryption from "utils/helper-encryption";
import VariableBuilder from "utils/variable-builder";
import axios from "axios";
import utf8 from 'utf8'
import HelperCollection from "utils/helper-collection";
import store from 'services/redux/store'
import { authenticationConstants } from 'services/redux/authentication/constants'
import UserService from 'services/user'
import { resetData } from 'services/redux/data/actions'
import { resetManagement } from 'services/redux/management/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import { configClient } from 'components/provider/ConfigProvider'
import { configKeys } from 'utils/config/keys'
import createAxiosClient from 'utils/request/axios'
import { SET_AUTHENTICATION } from 'services/redux/action-types'

export const graphqlOptions = {
    processVariables: 'processVariables'
};

/**
 *
 */
export default class GraphqlBuilder {
    options = {};
    queries = [];
    
    onStart = null;
    onSuccess = null;
    onError = null;
    onFinally = null;

    resultPath = null;
    filterResultWithoutGuid = false;
    
    cancelToken = axios.CancelToken;
    cancelTokenSource;
    
    useJWT = HelperEncryption.isJwtEnabled()

    /**
     *
     */
    constructor( options = {} ) {
        this.options = options;
    }
    
    /**
     *
     * @param onStart
     * @returns {this}
     */
    setOnStart = (onStart) => {
        this.onStart = onStart;
        
        return this;
    };
    
    /**
     *
     * @param onSuccess
     * @returns {this}
     */
    setOnSuccess = (onSuccess) => {
        this.onSuccess = onSuccess;
    
        return this;
    };
    
    /**
     *
     * @param onError
     * @returns {this}
     */
    setOnError = (onError) => {
        this.onError = onError;
        return this;
    };
    
    setOnFinally = (onFinally) => {
        this.onFinally = onFinally;
        return this;
    }

    /**
     *
     * @param resultPath
     * @param fallBack
     * @returns {this}
     */
    setResultPath = ( resultPath, fallBack = [] ) => {
        this.resultPath = resultPath;
        this.resultPathFallback = fallBack

        return this;
    }

    /**
     *
     * @param resultPath
     * @param fallBack
     * @returns {this}
     */
    setFilterResultWithoutGuid = ( filterWithoutGuid ) => {
        this.filterResultWithoutGuid = filterWithoutGuid;

        return this;
    }

    /**
     *
     * @param name
     * @param query
     * @param variables
     */
    addQuery = ( name, query, variables = {} ) => {
        const processVariables = _.get( this.options, graphqlOptions.processVariables, true );
        
        this.queries.push( {
            'name': name,
            'query': print( query ),
            'variables': processVariables ? VariableBuilder.form( variables ) : variables
        } );
    
        return this;
    };
    
    /**
     *
     * @param name
     */
    removeQuery = ( name ) => {
        this.queries = _.filter( this.queries, ( o ) => {
            return o.name !== name
        } );
    }
    
    /**
     *
     */
    execute = () => {
        if( this.onStart !== null && _.isFunction( this.onStart ) ) {
            this.onStart();
        }
    
        const baseUrl = configClient.url();
        if( baseUrl === null ) {
            if( _.isFunction( this.onError ) ) {
                this.onError({
                    error: 'no_base_url'
                });
            }
            
            if( _.isFunction( this.onFinally ) ) {
                this.onFinally();
            }
            return;
        }
    
        if( this.queries.length > 0 ) {
            this.cancelTokenSource = this.cancelToken.source()
            
            // Create the client!
            return createAxiosClient().then( axiosClient => {
                if( !this.useJWT ) {
                    return axiosClient
                        .post( configClient.get( configKeys.API_GRAPHQL_ENDPOINT ), this.queries, { cancelToken: this.cancelTokenSource?.token } )
                        .then( this.onThen );
                }
                
                const body = HelperEncryption.encode( JSON.stringify( this.queries ) );
                return axiosClient.post( configClient.get( configKeys.API_GRAPHQL_ENDPOINT ), body, { cancelToken: this.cancelTokenSource?.token } )
                           .then( this.onThen );
            }).catch( this.onCatch );
        } else {
            if( typeof this.onSuccess === 'function' ) {
                this.onSuccess( {} );
            }
            
            if( _.isFunction( this.onFinally ) ) {
                this.onFinally();
            }
        }
    };
    
    //
    onThen = ( response ) => {
        let data = _.get( response, 'data', null );
        
        if( this.useJWT ) {
           data = JSON.parse( utf8.decode( HelperEncryption.decode( data ) ) );
        }

        let foundErrors = {};

        // Create the results!
        let result = {};
        let queryErrorMessages = {};
        _.forEach( this.queries, (query) => {
            const name = query.name;

            const queryData = _.get( data, name, {} );

            if( "errors" in queryData ) {
                foundErrors[ name ] = queryData['errors'];
    
                // Add the message to the queryErrorMessages
                queryErrorMessages[ name ] = _.get( queryData, [ 'errors', 0, 'message' ], '');
            }

            result[ name ] = _.get( queryData, 'data', {} );
        });
        
        if( !_.isEmpty( foundErrors ) ) {
            const message = _.first( _.values( queryErrorMessages ) );

            // Overwrite response status
            response.status = 500;
            response.statusText = 'Internal Server Error';

            return this.onCatch( {
                config: response.config,
                message: message,
                queryMessages: queryErrorMessages,
                queryResults: result,
                queryErrors: foundErrors,
                response: {
                    status: 500,
                    data: response
                }
            } );
        }

        if( this.resultPath !== null ) {
            result = _.get( result, this.resultPath, this.resultPathFallback );
        }

        if( this.filterResultWithoutGuid ) {
            result = HelperCollection.filterWithoutGuid( result );
        }

        const value = this.onSuccess !== null && _.isFunction( this.onSuccess ) ? this.onSuccess( result, response ) : null;
        
        if( _.isFunction( this.onFinally ) ) {
            this.onFinally();
        }
        
        return value
    };
    
    onCatch = (error) => {
        if (error.response) {
            // Request was made but server responded with something
            // other than 2xx
            const errorDataMessage = _.get( error, ['response', 'data', 'data'], _.get( error, ['response', 'data' ], '' ) );
            switch( errorDataMessage ) {
                case "token_expired":
                    store.dispatch({
                        type: SET_AUTHENTICATION,
                        key: authenticationConstants.TOKEN_REVALIDATE,
                        payload: true
                    });
                    break;
                    
                case 'user_not_found_1':
                case 'error_not_active':
                    if( _.get( store.getState().authentication, authenticationConstants.LOGGED_IN, false ) === true ){
                        UserService.logout({
                            resetData: () => {
                                return store.dispatch(resetData());
                            },
                            resetManagement: () => {
                                return store.dispatch(resetManagement());
                            },
                            resetAuthentication: () => {
                                return store.dispatch(resetAuthentication());
                            },
                            resetCart: () => {
                                return store.dispatch(resetCart());
                            },
                            restart: true
                        });
                    }
                    break;
            }
    
            if( this.onError !== null && _.isFunction( this.onError ) ) {
                return this.onError( error, {
                    error: errorDataMessage
                });
            }
            
            if( _.isFunction( this.onFinally ) ) {
                this.onFinally();
            }
        }
        
        // Something else happened while setting up the request
        // triggered the error
        if( this.onError !== null && _.isFunction( this.onError ) ) {
            return this.onError( error, {
                error: error.message
            });
        }
        
        if( _.isFunction( this.onFinally ) ) {
            this.onFinally();
        }
    };
    
    retry = () => {
        return this.execute();
    };
}
